import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useAssignProductMutation,
  useRemoveShopProductSkuMutation,
  useShopProductsQuery,
  useShopQuery,
  useUpdateShopProductSkuMutation,
} from "../api/queries";
import { AssignProductModal } from "../ui/AssignProductModal";
import { EditShopProductSkuModal } from "../ui/EditShopProductSkuModal";
import { ShopProductsTable } from "../ui/ShopProductsTable";
import { ShopProductsToolbar } from "../ui/ShopProductsToolbar";

const DEFAULT_LIMIT = 20;

export function ShopProductsPage() {
  const { id: shopId } = useParams({ from: "/app/shops/$id/products" });
  const navigate = useNavigate();
  const [page, setPage] = useState({ limit: DEFAULT_LIMIT, offset: 0 });
  const [filter, setFilter] = useState({ search: "", status: "" });
  const [assignOpen, setAssignOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<{
    id: string;
    shopId: string;
    shopProductId: string;
    catalogProductId: string;
    catalogSkuId: string;
    productName: string;
    thumbnailUrl: string | null;
    sku: string;
    barcode: string | null;
    price: number;
    cost: number | null;
    currency: string;
    isActive: boolean;
    productStatus: string;
    updatedAt: string;
  } | null>(null);

  const shopQuery = useShopQuery(shopId ?? "");
  const productsQuery = useShopProductsQuery(shopId ?? "", {
    limit: page.limit,
    offset: page.offset,
    status: filter.status || undefined,
    search: filter.search?.trim() || undefined,
  });
  const assignMutation = useAssignProductMutation(shopId ?? "");
  const updateSkuMutation = useUpdateShopProductSkuMutation(shopId ?? "");
  const removeSkuMutation = useRemoveShopProductSkuMutation(shopId ?? "");

  const canCreate = useActionPermission(["catalog:create"]);
  const canUpdate = useActionPermission(["catalog:update"]);
  const canDelete = useActionPermission(["catalog:delete"]);

  const shop = shopQuery.data;
  const listResult = productsQuery.data;
  const data = listResult?.data ?? [];
  const meta = listResult?.meta ?? {
    total: 0,
    limit: page.limit,
    offset: page.offset,
  };
  const alreadyAssignedProductIds = Array.from(
    new Set(data.map((r) => r.catalogProductId)),
  );

  const handleAssign = async (
    body: Parameters<typeof assignMutation.mutateAsync>[0],
  ) => {
    try {
      await assignMutation.mutateAsync(body);
      toast.success("ມອບສິນຄ້າສຳເລັດ");
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? "ມອບສິນຄ້າລົ້ມເຫຼວ");
      throw e;
    }
  };

  const handleUpdateSku = async (body: {
    sku: string;
    barcode: string | null;
    price: number;
    cost: number | null;
    currency: "LAK" | "THB" | "USD";
    isActive: boolean;
  }) => {
    if (!editingRow) return;
    try {
      await updateSkuMutation.mutateAsync({
        shopProductSkuId: editingRow.id,
        body,
      });
      toast.success("ບັນທຶກສຳເລັດ");
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? "ບັນທຶກລົ້ມເຫຼວ");
      throw e;
    }
  };

  const handleDeactivate = async (shopProductSkuId: string) => {
    try {
      await removeSkuMutation.mutateAsync({
        shopProductSkuId,
        mode: "soft",
      });
      toast.success("ປິດการขาย SKU ແລ້ວ");
    } catch {
      // fetcher แสดง toast error แล้ว
    }
  };

  const handleRemove = async (shopProductSkuId: string) => {
    try {
      await removeSkuMutation.mutateAsync({
        shopProductSkuId,
        mode: "hard",
      });
      toast.success("ລຶບອອກຈາກຮ້ານແລ້ວ");
    } catch {
      // fetcher แสดง toast error แล้ว
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຮ້ານຄ້າ", to: "/app/shops" },
            {
              label: shop?.name ?? "ຮ້ານ",
              to: "/app/shops/$id",
              params: { id: shopId ?? "" },
            },
            { label: "ສິນຄ້າຂອງຮ້ານ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ສິນຄ້າຂອງຮ້ານ</h2>
            <p className="text-muted-foreground">
              {shop ? `${shop.name}` : "ກຳລັງໂຫຼດ..."}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              navigate({ to: "/app/shops/$id", params: { id: shopId ?? "" } })
            }
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="flex flex-col gap-4 rounded-xl border border-border/80 bg-card shadow-sm">
          <ShopProductsToolbar
            search={filter.search}
            status={filter.status}
            onSearchChange={(v) => {
              setFilter((f) => ({ ...f, search: v }));
              setPage((p) => ({ ...p, offset: 0 }));
            }}
            onStatusChange={(v) => {
              setFilter((f) => ({ ...f, status: v }));
              setPage((p) => ({ ...p, offset: 0 }));
            }}
            onAssignClick={() => setAssignOpen(true)}
            canAssign={!!canCreate}
          />
          <ShopProductsTable
            canUpdate={!!canUpdate}
            canDelete={!!canDelete}
            isLoading={productsQuery.isLoading}
            data={data}
            offset={page.offset}
            limit={page.limit}
            totalCount={meta.total}
            onEdit={(row) => setEditingRow(row)}
            onDeactivate={handleDeactivate}
            onRemove={handleRemove}
            onPaginationChange={(offset, limit) =>
              setPage((p) => ({ ...p, offset, limit }))
            }
          />
        </div>

        <AssignProductModal
          shopId={shopId ?? ""}
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          onSubmit={handleAssign}
          alreadyAssignedProductIds={alreadyAssignedProductIds}
          shopDefaultCurrency={shop?.defaultCurrency ?? "LAK"}
          enforceSingleShopSkuCurrency={
            shop?.enforceSingleShopSkuCurrency ?? false
          }
        />

        <EditShopProductSkuModal
          open={!!editingRow}
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSubmit={handleUpdateSku}
          shopDefaultCurrency={shop?.defaultCurrency ?? "LAK"}
          enforceSingleShopSkuCurrency={
            shop?.enforceSingleShopSkuCurrency ?? false
          }
        />
      </Main>
    </>
  );
}
